import { Flex, Segmented, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { listJobCards, type JobCard } from './api';
import { JOB_STATUS_LABEL, JOB_TYPE_LABEL, jobTone } from './status';

const { Text } = Typography;

/** The four questions a service manager asks, in the order they ask them. */
type View = 'open' | 'late' | 'billing' | 'comeback';

export function JobBoardPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('open');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['job-cards', view, search], (page) =>
    listJobCards({
      ...page,
      q: search,
      ...(view === 'open' ? { open: true } : {}),
      ...(view === 'late' ? { late: true } : {}),
      ...(view === 'billing' ? { status: 'READY_FOR_BILLING' as const } : {}),
      ...(view === 'comeback' ? { isComeback: true } : {}),
    }),
  );

  const emptyFor: Record<View, string> = {
    open: t('workshop.emptyOpen'),
    late: t('workshop.emptyLate'),
    billing: t('workshop.emptyBilling'),
    comeback: t('workshop.emptyComeback'),
  };

  return (
    <Flex vertical gap={12}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'open', label: t('workshop.viewOpen') },
          { value: 'late', label: t('workshop.viewLate') },
          { value: 'billing', label: t('workshop.viewBilling') },
          { value: 'comeback', label: t('workshop.viewComeback') },
        ]}
      />

      <ListPage<JobCard>
        title={t('workshop.boardTitle')}
        searchPlaceholder={t('workshop.boardSearch')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('workshop.noResults') : emptyFor[view]}
        columns={[
          {
            title: t('workshop.fields.jobNo'),
            dataIndex: 'jobNo',
            width: 170,
            render: (value: string) => <span className="tabular">{value}</span>,
          },
          {
            title: t('vehicles.fields.plateNo'),
            dataIndex: 'plateNo',
            width: 130,
            render: (value: string | null, row: JobCard) => (
              <Flex vertical gap={2}>
                <Text strong>{orDash(value)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {orDash(row.model)}
                </Text>
              </Flex>
            ),
          },
          { title: t('sales.fields.customer'), dataIndex: 'customerName' },
          {
            title: t('sales.fields.status'),
            dataIndex: 'status',
            // Two chips that wrap, not two chips side by side. Customer is the
            // only flexible column here, so every pixel reserved above what a
            // chip needs is taken straight off the customer's name.
            width: 150,
            render: (status: JobCard['status'], row: JobCard) => (
              <Flex gap={4} wrap>
                <Tag color={jobTone(status)}>{JOB_STATUS_LABEL[status]}</Tag>
                {row.isComeback && <Tag color="error">{t('workshop.comeback')}</Tag>}
              </Flex>
            ),
          },
          {
            title: t('workshop.fields.jobType'),
            dataIndex: 'jobType',
            width: 110,
            render: (type: JobCard['jobType']) => JOB_TYPE_LABEL[type],
          },
          {
            title: t('workshop.fields.technicians'),
            width: 170,
            render: (_: unknown, row: JobCard) =>
              row.technicians.length === 0 ? (
                <Text type="warning">{t('workshop.nobody')}</Text>
              ) : (
                <Flex gap={4} wrap>
                  {row.technicians.map((tech) => (
                    <Tag key={tech.uid} color={tech.running ? 'processing' : undefined}>
                      {tech.name}
                    </Tag>
                  ))}
                </Flex>
              ),
          },
          {
            title: t('workshop.fields.promised'),
            dataIndex: 'promisedAt',
            width: 160,
            render: (value: string | null, row: JobCard) =>
              value === null ? (
                <Text type="secondary">{t('workshop.noPromise')}</Text>
              ) : (
                // The KPI: what the customer was told, and whether it held.
                <Text type={row.isLate ? 'danger' : undefined} strong={row.isLate}>
                  {formatDateTime(value)}
                </Text>
              ),
          },
          {
            title: t('workshop.fields.total'),
            dataIndex: 'billableTotal',
            width: 120,
            align: 'right' as const,
            render: (value: string) => formatMoney(value),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/workshop/job-cards/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{orDash(row.plateNo)}</Text>
              <Tag color={jobTone(row.status)} style={{ marginInlineEnd: 0 }}>
                {JOB_STATUS_LABEL[row.status]}
              </Tag>
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.customerName}
              </Text>
              {row.isLate && <Text type="danger">{t('workshop.late')}</Text>}
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
